import React from 'react';
import { Alert } from '@planne-software/uni/Alert';
import { Button } from '@planne-software/uni/Button';
import { IconArrowRight } from '@planne-software/uni/icons';

import { useTranslate } from '@src/core/hooks';
import { SecurePurchaseNote } from '@src/common/ui/SecurePurchaseNote';

import { useAdditionalInfoCollect } from '../hooks/useAdditionalInfoCollect';
import {
  AdditionalInfoFooter,
  AdditionalInfoHeader,
  AdditionalInfoRoot,
  AdditionalInfoSubtitle,
  AdditionalInfoTitle,
} from '../styles';

import { AdditionalInfoModal } from './AdditionalInfoModal';
import { SaleAccordion } from './SaleAccordion';

type AdditionalInfoCardProps = {
  onCompleted: () => void;
};

export const AdditionalInfoCard = (props: AdditionalInfoCardProps) => {
  const { onCompleted } = props;
  const { i18n } = useTranslate();
  const collect = useAdditionalInfoCollect({ onCompleted });

  return (
    <AdditionalInfoRoot data-testid='additional-info-card'>
      <AdditionalInfoHeader>
        <AdditionalInfoTitle>{i18n('checkout_additional_info_title')}</AdditionalInfoTitle>
        <AdditionalInfoSubtitle>{i18n('checkout_additional_info_subtitle')}</AdditionalInfoSubtitle>
      </AdditionalInfoHeader>

      {collect.hasErrors ? (
        <Alert severity='error' data-testid='additional-info-error-alert'>
          {i18n('checkout_additional_info_error_alert')}
        </Alert>
      ) : null}

      {collect.saleGroups.map((group) => (
        <SaleAccordion
          key={group.saleItemId}
          group={group}
          summary={collect.summaryFor(group.saleItemId)}
          values={collect.values}
          groupsWithErrors={collect.groupsWithErrors}
          expanded={collect.expandedSaleId === group.saleItemId}
          onToggle={collect.toggleSale}
          onFillTariff={collect.openTariff}
          onEditTariff={collect.openTariffForEdit}
        />
      ))}

      <AdditionalInfoFooter>
        <SecurePurchaseNote label={i18n('checkout_secure_purchase')} />

        <Button
          size='lg'
          onClick={collect.submit}
          disabled={!collect.canContinue}
          loading={collect.submitting}
          endSlot={<IconArrowRight size={20} aria-hidden />}
        >
          {i18n('checkout_additional_info_continue')}
        </Button>
      </AdditionalInfoFooter>

      <AdditionalInfoModal
        open={collect.modal.open}
        mode={collect.modal.mode}
        queue={collect.modal.queue}
        productNameFor={collect.modal.productNameFor}
        onStepChange={collect.modal.onStepChange}
        storeErrors={collect.storeErrors}
        values={collect.values}
        onSaveFormInput={collect.saveFormInput}
        onClose={collect.closeModal}
      />
    </AdditionalInfoRoot>
  );
};

export default AdditionalInfoCard;
